import React, { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Select, AutoComplete } from 'antd'
import * as Icons from '@ant-design/icons'
import styled from 'styled-components'

import {
  FormInput,
  FormRules,
  FormItem,
  WarningMessage
} from 'portal/components/RecordEditing'
import { EWC_CODES } from 'portal/lib/constants'
import {
  CheckQuantity,
  CheckVolume,
  CheckWasteDescription,
  CheckWeight
} from 'portal/lib/functions/SimpleDataValidation'
import { Spacer } from 'portal/lib/primitives'

import { useCountryDrivenValues } from 'portal/lib/hooks'

export function Waste({
  isImperial = false,
  lineItemDataSourceTracking = {},
  form
}) {
  const [initialContainerType, setInitialContainerType] = useState(null)
  const [containerTypeWarning, setContainerTypeWarning] = useState(null)

  const { wasteFields } = useCountryDrivenValues()

  useEffect(() => {
    form.register('knownContainerType')
    form.register('wasteCode')
    form.register('volumeEstimated')
    form.register('weightEstimated')
  }, [form])

  const { containerTypes: allContainerTypes } = useSelector(
    state => state.containerTypes
  )

  const containerTypesOptions = allContainerTypes
    .filter(ct => ct !== 'Unknown')
    .map(ct => ({
      label: ct,
      value: ct,
      key: ct
    }))

  // Work around the BugFix (below) issue where initial render does not have form values
  useEffect(() => {
    if (initialContainerType || Object.keys(form.getValues()).length === 0) {
      return
    }

    const initialValue = form.getValues('knownContainerType')
    setInitialContainerType(initialValue)
  }, [form, initialContainerType, setInitialContainerType])

  // Not watching KnownContainerType because it causes re-render and updates AutoCompleted, meaning user changes immediately are ignored
  const wasteCode = form.watch('wasteCode')
  const volumeEstimated = form.watch('volumeEstimated')
  const weightEstimated = form.watch('weightEstimated')

  const ewcCodeOptions = EWC_CODES.map(code => ({
    label: `${code.ewcCode} - ${code.description}`,
    value: code.ewcCode,
    key: code.ewcCode
  }))

  const setWasteCode = value => {
    form.setValue('wasteCode', value, {
      shouldDirty: true
    })
  }

  const setKnownContainerType = value => {
    form.setValue('knownContainerType', value, {
      shouldDirty: true
    })

    if (showWarningIfUnilistedContainerType(value)) {
      return
    }

    showWarningIfContainerTypeHasChanged(value)
  }

  const showWarningIfUnilistedContainerType = useCallback(
    value => {
      if (
        value !== null &&
        value !== '' &&
        allContainerTypes.filter(ct => ct === value).length === 0
      ) {
        setContainerTypeWarning(
          'Warning: Using an unlisted Container Type will remove weight/volume if ticket data is missing.'
        )
        return true
      }

      return false
    },
    [allContainerTypes]
  )

  const showWarningIfContainerTypeHasChanged = useCallback(
    value => {
      if (initialContainerType === value) {
        setContainerTypeWarning(null)
      } else if (value === '' || value === null) {
        setContainerTypeWarning(
          'Warning: Removing Container Type will remove weight/volume if ticket data is missing.'
        )
      } else {
        setContainerTypeWarning(
          'Warning: Updating Container Type may change weight/volume if ticket data is missing.'
        )
      }
    },
    [initialContainerType]
  )

  const VolumeLabelBody = !isImperial ? (
    <>
      Volume (m<sup>3</sup>)
    </>
  ) : (
    'Volume'
  )

  const WeightLabelBody = !isImperial ? 'Weight (t)' : 'Weight'

  // BugFix: Sometime the form does not get initialised with the default values
  // so we trigger validation to force it to load the default values
  if (Object.keys(form.getValues()).length === 0) {
    form.trigger()
    return null
  }

  return (
    <>
      <FormInput.Hidden name="id" />
      <FormItem
        label="Container type"
        valueAdd={{
          fieldName: 'knownContainerType',
          ...lineItemDataSourceTracking?.knownContainerType
        }}
      >
        <AutoComplete
          name="knownContainerType"
          autoFocus
          allowClear
          defaultValue={initialContainerType}
          options={containerTypesOptions}
          placeholder="Enter or select Container Type"
          filterOption={(inputValue, option) =>
            option.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
          }
          onClear={() => {
            setKnownContainerType('')
          }}
          onChange={value => {
            setKnownContainerType(value)
          }}
          suffixIcon={<Icons.SearchOutlined />}
        />
        {containerTypeWarning && (
          <WarningMessage>{containerTypeWarning}</WarningMessage>
        )}
      </FormItem>

      {wasteFields && wasteFields.wasteDescription && (
        <FormItem
          label="Waste description"
          valueAdd={{
            fieldName: 'wasteDescription',
            ...lineItemDataSourceTracking?.wasteDescription
          }}
        >
          <FormInput.Text
            label="Waste description"
            name="wasteDescription"
            simpleDataValidationRules={CheckWasteDescription}
          />
        </FormItem>
      )}

      {wasteFields && wasteFields.wasteCode && (
        <FormItem
          label="Waste code (EWC)"
          valueAdd={{
            fieldName: 'wasteCode',
            ...lineItemDataSourceTracking?.wasteCode
          }}
        >
          <Select
            name="wasteCode"
            allowClear
            showSearch
            options={ewcCodeOptions}
            value={wasteCode}
            placeholder="Enter or select EWC code"
            filterOption={(inputValue, option) =>
              option.value.toUpperCase().indexOf(inputValue.toUpperCase()) !==
              -1
            }
            onChange={value => {
              setWasteCode(value)
            }}
          />
        </FormItem>
      )}
      <Spacer.Small />
      {wasteFields && wasteFields.volume && (
        <FormItem
          label={VolumeLabelBody}
          valueAdd={{
            fieldName: 'volume',
            ...lineItemDataSourceTracking?.volume
          }}
        >
          <MultiInputWrapper>
            <FormInput.Text
              label="Volume"
              name="volume"
              type="number"
              placeholder={volumeEstimated ? `est. ${volumeEstimated}` : ''}
              rules={FormRules.OptionalPositiveDecimal()}
              simpleDataValidationRules={v =>
                CheckVolume(v, isImperial ? 'CubicYard' : 'CubicMeter')
              }
              inputUnit={isImperial ? 'cubic yard' : null}
            />
          </MultiInputWrapper>
        </FormItem>
      )}

      {wasteFields && wasteFields.weight && (
        <FormItem
          label={WeightLabelBody}
          valueAdd={{
            fieldName: 'weight',
            ...lineItemDataSourceTracking?.weight
          }}
        >
          <MultiInputWrapper>
            <FormInput.Text
              label="Weight"
              name="weight"
              type="number"
              placeholder={weightEstimated ? `est. ${weightEstimated}` : ''}
              rules={FormRules.OptionalPositiveDecimal()}
              simpleDataValidationRules={v =>
                CheckWeight(v, isImperial ? 'Pound' : 'Tonne')
              }
              inputUnit={isImperial ? 'lbs' : null}
            />
          </MultiInputWrapper>
        </FormItem>
      )}
      <Spacer.Small />
      {wasteFields && wasteFields.quantity && (
        <FormItem
          label="Quantity"
          valueAdd={{
            fieldName: 'quantity',
            ...lineItemDataSourceTracking?.quantity
          }}
        >
          <MultiInputWrapper>
            <FormInput.Text
              label="Quantity"
              name="quantity"
              type="number"
              rules={FormRules.OptionalPositiveDecimal()}
              simpleDataValidationRules={CheckQuantity}
            />
          </MultiInputWrapper>
        </FormItem>
      )}
    </>
  )
}

export const MultiInputWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  min-width: 15rem;
  gap: 0 0.5rem;
  flex: 0 1 auto;
`
